import React from 'react';
import { ExportV2LimitedLineup } from '../../../../../../../../shared/types/exportV2';
import { isoDate } from '../../../../../../../../shared/lib/time';
import {
  DailyProgramEvent,
  DailyProgramVenue,
  DailyProgramVenueMealPeriod,
} from '../../../../../../../../shared/types/dailyProgram';
import { useData } from '../../context/DataContext';
import { useQueryStrings } from '../../context/QueryStringContext';

export interface SimilarTimes {
  date: string;
  times: string[];
  mealPeriods: DailyProgramVenueMealPeriod[];
}

interface SimilarTimesHocChildProps {
  similarTimes: SimilarTimes[];
}

interface SimilarTimesHocPropsBase {}

interface SimilarTimesHocPropsEvent {
  type: 'events';
  item: DailyProgramEvent;
}

interface SimilarTimesHocPropsVenue {
  type: 'venues';
  item: DailyProgramVenue;
}

export type SimilarTimesHocPropsBasic = SimilarTimesHocPropsBase &
  (SimilarTimesHocPropsEvent | SimilarTimesHocPropsVenue);

type SimilarTimesHocProps = SimilarTimesHocPropsBasic & {
  children: (props: SimilarTimesHocChildProps) => React.ReactNode;
};

export default function SimilarTimesHoc(props: SimilarTimesHocProps) {
  const { type, item, children } = props;
  const { todayLineup, data } = useData();
  const { qs } = useQueryStrings();

  const similarTimes: SimilarTimes[] = [];

  function extractTimes(lineup: ExportV2LimitedLineup) {
    return type === 'events'
      ? lineup.events.filter(e => e.templateId === item.templateId).map(e => e.timeString)
      : lineup.venues.find(v => v.templateId === item.templateId)?.timeStrings || [];
  }

  function extractMealPeriods(lineup: ExportV2LimitedLineup) {
    return lineup.venues.find(v => v.templateId === item.templateId)?.mealPeriodStrings || [];
  }

  const todayTimes = extractTimes(todayLineup);
  const todayMealPeriods = extractMealPeriods(todayLineup);

  if (todayTimes.length > 0) {
    similarTimes.push({
      date: data.appConfig.TODAY_LABEL,
      times: todayTimes,
      mealPeriods: todayMealPeriods,
    });
  }

  const today = todayLineup.date;
  const nextDay = new Date(today);
  nextDay.setUTCDate(nextDay.getUTCDate() + 1);
  const tomorrowLineup = data.lineup[isoDate(nextDay)];

  if (tomorrowLineup) {
    const tomorrowTimes = extractTimes(tomorrowLineup);
    const tomorrowMealPeriods = extractMealPeriods(tomorrowLineup);

    if (tomorrowTimes.length > 0) {
      similarTimes.push({
        date: data.appConfig.TOMORROW_LABEL,
        times: tomorrowTimes,
        mealPeriods: tomorrowMealPeriods,
      });
    }
  }

  nextDay.setUTCDate(nextDay.getUTCDate() + 1);
  let nextLineup = data.lineup[isoDate(nextDay)];

  while (nextLineup) {
    const nextTimes = extractTimes(nextLineup);
    const nextMealPeriods = extractMealPeriods(nextLineup);

    if (nextTimes.length > 0) {
      similarTimes.push({
        date: new Date(nextLineup.date.split('-').join('/')).toLocaleDateString(qs.lang, {
          month: 'short',
          day: 'numeric',
          weekday: 'short',
        }),
        times: nextTimes,
        mealPeriods: nextMealPeriods,
      });
    }

    nextDay.setUTCDate(nextDay.getUTCDate() + 1);
    nextLineup = data.lineup[isoDate(nextDay)];
  }

  const childProps: SimilarTimesHocChildProps = {
    similarTimes,
  };

  return <>{children(childProps)}</>;
}
